import React from "react";
import {
    Button,
    Form,
    Headline,
    InputControl,
    Loading,
    RequiredText,
    StyleFunction,
    TextInput,
    useThemedStyle,
} from "@venuepos/react-common";
import { useTranslation } from "locales";
import { View } from "react-native";
import { FiskalyEstablishmentInput } from "../fiskaly-form";

export function FiskalyEstablishmentForm({
    form,
    onSubmit,
    onClear,
}: {
    form: Form<FiskalyEstablishmentInput>;
    onSubmit: () => void;
    onClear: () => void;
}) {
    const [t] = useTranslation();
    const styles = useThemedStyle(styleFunc);
    const [{ values, errors }, { setValue, handleSubmit }] = form;

    if (!values) {
        return <Loading />;
    }

    return (
        <>
            <Headline size="h4">
                {t(
                    "backoffice.integration.fiskaly.edit_establishment",
                    "Add/Edit establishment"
                )}
            </Headline>
            <InputControl error={errors.designation}>
                <TextInput
                    label={t(
                        "backoffice.integration.fiskaly.establishment_designation",
                        "Designation"
                    )}
                    value={values.designation}
                    onChangeText={newText => setValue("designation", newText)}
                    required
                />
            </InputControl>
            <View style={styles.inputRow}>
                <InputControl error={errors.street} style={styles.street}>
                    <TextInput
                        label={t("backoffice.common.street", "Street")}
                        value={values.street}
                        onChangeText={newText => setValue("street", newText)}
                        required
                    />
                </InputControl>
                <InputControl
                    error={errors.houseNumber}
                    style={styles.houseNumber}
                >
                    <TextInput
                        label={t(
                            "backoffice.common.house_number",
                            "House number"
                        )}
                        value={values.houseNumber}
                        onChangeText={newText =>
                            setValue("houseNumber", newText)
                        }
                        required
                    />
                </InputControl>
                <InputControl
                    error={errors.houseNumberSuffix}
                    style={styles.houseNumberSuffix}
                >
                    <TextInput
                        label={t("backoffice.common.suffix", "Suffix")}
                        value={values.houseNumberSuffix}
                        onChangeText={newText =>
                            setValue("houseNumberSuffix", newText)
                        }
                        maxLength={20}
                    />
                </InputControl>
            </View>
            <View style={styles.inputRow}>
                <InputControl error={errors.zip} style={styles.zip}>
                    <TextInput
                        label={t("backoffice.common.zip", "Zip")}
                        value={values.zip}
                        onChangeText={newText => setValue("zip", newText)}
                        maxLength={5}
                        required
                    />
                </InputControl>
                <InputControl error={errors.town} style={styles.city}>
                    <TextInput
                        label={t("backoffice.common.city", "City")}
                        value={values.town}
                        onChangeText={newText => setValue("town", newText)}
                        required
                    />
                </InputControl>
            </View>
            <InputControl error={errors.remarks}>
                <TextInput
                    label={t(
                        "backoffice.integration.fiskaly.remarks",
                        "Remarks"
                    )}
                    value={values.remarks}
                    onChangeText={newText => setValue("remarks", newText)}
                />
            </InputControl>
            <InputControl error={errors.addressAdditional}>
                <TextInput
                    label={t(
                        "backoffice.integration.fiskaly.establishment_additional_address_data",
                        "Additional address data"
                    )}
                    value={values.addressAdditional}
                    onChangeText={newText =>
                        setValue("addressAdditional", newText)
                    }
                />
            </InputControl>

            <View style={styles.inputRow}>
                <Button
                    onPress={handleSubmit(onSubmit)}
                    size="small"
                    type="primary"
                >
                    {t("backoffice.common.save", "Save")}
                </Button>
                <Button
                    onPress={() => {
                        if (onClear) {
                            onClear();
                        }
                    }}
                    size="small"
                >
                    {t("backoffice.common.clear", "Clear form")}
                </Button>
            </View>
            <RequiredText />
        </>
    );
}

const styleFunc: StyleFunction = theme => ({
    inputRow: {
        ...theme.styles.row,
        columnGap: theme.spacingScale,
    },
    street: {
        ...theme.styles.flex3,
    },
    houseNumber: {
        ...theme.styles.flex2,
    },
    houseNumberSuffix: {
        ...theme.styles.flex1,
    },
    zip: {
        ...theme.styles.flex1,
    },
    city: {
        ...theme.styles.flex3,
    },
});
